import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import { useAuth } from '@/lib/auth-context'
import {
  describeGaps,
  describeValidationError,
  getOrganizationProfile,
  updateOrganizationProfile,
  type OrganizationProfileDto,
  type OrganizationProfileGap,
  type UpdateOrganizationProfileRequest,
} from '@/lib/api/v2/organization-profile'

/**
 * The organization's identity, as it comes out on its documents.
 *
 * Until this screen, the profile could only be filled through the API. The dashboard
 * banner told the user their documents were going out without an IBAN, a VAT number
 * or an address -- and linked to a Settings page that offered no way to fix it. A
 * quote issued in the meantime carries the company name, the sender's login address,
 * and nothing else: it is not a compliant Belgian document.
 *
 * Every field is a field that gets PRINTED. That is why the form says where, rather
 * than repeating the label.
 */

/** Belgium is the only market today. One literal, one place. */
export const DEFAULT_COUNTRY = 'BE'

type FormState = {
  street: string
  number: string
  box: string
  postalCode: string
  city: string
  country: string
  vatNumber: string
  email: string
  phone: string
  website: string
  logoUrl: string
  bankAccounts: string[]
}

const EMPTY: FormState = {
  street: '',
  number: '',
  box: '',
  postalCode: '',
  city: '',
  country: DEFAULT_COUNTRY,
  vatNumber: '',
  email: '',
  phone: '',
  website: '',
  logoUrl: '',
  bankAccounts: [''],
}

export function toFormState(profile: OrganizationProfileDto): FormState {
  return {
    street: profile.street ?? '',
    number: profile.number ?? '',
    box: profile.box ?? '',
    postalCode: profile.postalCode ?? '',
    city: profile.city ?? '',
    country: profile.country || DEFAULT_COUNTRY,
    vatNumber: profile.vatNumber ?? '',
    email: profile.email ?? '',
    phone: profile.phone ?? '',
    website: profile.website ?? '',
    logoUrl: profile.logoUrl ?? '',
    // Always at least one row: an empty list would leave the user with no field to
    // type an IBAN into, on the one gap that matters most.
    bankAccounts: profile.bankAccounts?.length ? [...profile.bankAccounts] : [''],
  }
}

/**
 * What the ENDPOINT refuses to save, which is not the same list as what the
 * DOCUMENT lacks.
 *
 * UpdateOrganizationProfileRequest marks street, number, postal code, city, country,
 * VAT number, e-mail AND phone as [Required]. MissingForDocuments reports four gaps
 * and says nothing about the phone or the e-mail. Found in browser QA on 2026-09-08:
 * fill exactly the four gaps the banner names, hit save, and the call comes back 400
 * because the phone is empty -- with no way to know which field.
 *
 * So the form checks the endpoint's list itself and names the offenders, rather than
 * sending a request that cannot succeed.
 */
const REQUIRED_BY_ENDPOINT: { field: keyof FormState; label: string }[] = [
  { field: 'street', label: 'la rue' },
  { field: 'number', label: 'le numéro' },
  { field: 'postalCode', label: 'le code postal' },
  { field: 'city', label: 'la commune' },
  // Not 'country': the request defaults it to DEFAULT_COUNTRY, so blocking the save on
  // an empty one would refuse a value the form was about to supply itself.
  { field: 'vatNumber', label: 'le numéro de TVA' },
  { field: 'email', label: "l'e-mail de la société" },
  { field: 'phone', label: 'le téléphone' },
]

export function missingRequired(form: FormState): string[] {
  return REQUIRED_BY_ENDPOINT
    .filter(({ field }) => !String(form[field]).trim())
    .map(({ label }) => label)
}

/**
 * The gaps the backend would report for this form, computed locally so the warning
 * follows what the user is typing instead of what was last saved. Mirrors
 * OrganizationProfile.MissingForDocuments -- deliberately, and the backend stays the
 * authority: this only decides what the screen says before saving.
 */
export function localGaps(form: FormState, organizationName: string): OrganizationProfileGap[] {
  const gaps: OrganizationProfileGap[] = []
  if (!organizationName.trim()) gaps.push('name')
  if (!form.street.trim() || !form.number.trim() || !form.postalCode.trim() || !form.city.trim()) {
    gaps.push('address')
  }
  if (!form.vatNumber.trim()) gaps.push('vat_number')
  if (!form.bankAccounts.some(a => a.trim())) gaps.push('bank_account')
  return gaps
}

/**
 * The body of the PUT.
 *
 * Extracted and exported because the endpoint REPLACES the whole profile: a key
 * missing from this object is a field wiped on the server, silently, on data that is
 * printed on every invoice. That failure mode deserves a test of its own, and a test
 * cannot reach an object literal buried in an event handler.
 *
 * Blank optional fields go out as `null`, not as `''`: the backend validates
 * logoUrl and website with [Url], which accepts null and rejects an empty string.
 */
/**
 * A URL that is safe to put in this form, and safe to send.
 *
 * Not cosmetic validation. These two fields are interpolated UNESCAPED into the
 * Scriban templates the backend renders to PDF -- `<img src="{{ issuer.logo_url }}">`
 * sits inside an attribute, and TemplateRenderer does no HTML encoding. A value
 * carrying a quote can close that attribute and inject markup into a document that
 * Chromium then renders with JavaScript enabled. The server-side guard is the real
 * gate (UpdateOrganizationProfileRequest); this one stops the payload at the screen
 * that offers the field, and tells the user why instead of returning a bare 400.
 */
export function urlProblem(value: string): string | null {
  const v = value.trim()
  if (!v) return null
  if (/["'<>]/.test(v)) return 'Cette adresse contient un caractère interdit (guillemet ou chevron).'
  let parsed: URL
  try {
    parsed = new URL(v)
  } catch {
    return 'Adresse invalide. Elle doit commencer par https://'
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return 'Seules les adresses http:// et https:// sont acceptées.'
  }
  return null
}

export function toUpdateRequest(form: FormState): UpdateOrganizationProfileRequest {
  return {
    street: form.street.trim(),
    number: form.number.trim(),
    box: form.box.trim() || null,
    postalCode: form.postalCode.trim(),
    city: form.city.trim(),
    country: form.country.trim() || DEFAULT_COUNTRY,
    vatNumber: form.vatNumber.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    logoUrl: form.logoUrl.trim() || null,
    website: form.website.trim() || null,
    // A blank row is not an account. Clearing the only one DOES wipe the IBAN, which
    // is allowed on purpose -- an organization may genuinely not have one yet, and the
    // gap warning says so before and after the save. It is locked by a test so the
    // behaviour is a decision rather than an accident.
    bankAccounts: form.bankAccounts.map(a => a.trim()).filter(Boolean),
  }
}

export function OrganizationProfileSettings() {
  const { user } = useAuth()
  const organizationId = user?.selectedOrganizationId ?? null

  const [profile, setProfile] = useState<OrganizationProfileDto | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [logoBroken, setLogoBroken] = useState(false)

  const load = useCallback(async () => {
    // No organization means nothing to load -- and leaving `loading` true would pin
    // the screen on "Chargement…" for ever.
    if (!organizationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadFailed(false)
    try {
      const p = await getOrganizationProfile(organizationId)
      setProfile(p)
      setForm(toFormState(p))
    } catch {
      // The gaps warning must NOT be drawn from an empty form: a transient 500 would
      // then tell a correctly configured company that its documents go out without an
      // IBAN, a VAT number and an address. A failure to read is not a finding.
      setLoadFailed(true)
      showErrorToast("Impossible de charger le profil de la société.")
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => { load() }, [load])

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    if (field === 'logoUrl') setLogoBroken(false)
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function addAccount() {
    setForm(prev => ({ ...prev, bankAccounts: [...prev.bankAccounts, ''] }))
  }

  function removeAccount(index: number) {
    setForm(prev => ({
      ...prev,
      // Never leave zero rows: the IBAN is the gap that matters most, and a section
      // with no field is a section with no way to fix it.
      bankAccounts: prev.bankAccounts.length <= 1 ? [''] : prev.bankAccounts.filter((_, i) => i !== index),
    }))
  }

  function setAccount(index: number, value: string) {
    setForm(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts.map((a, i) => (i === index ? value : a)),
    }))
  }

  async function handleSave() {
    if (!organizationId) {
      showErrorToast('Aucune organisation sélectionnée.')
      return
    }

    const missing = missingRequired(form)
    if (missing.length > 0) {
      showErrorToast(`Il manque ${missing.join(', ')}.`)
      return
    }

    const badUrl = urlProblem(form.logoUrl) ?? urlProblem(form.website)
    if (badUrl) {
      showErrorToast(badUrl)
      return
    }

    setSaving(true)
    try {
      const saved = await updateOrganizationProfile(organizationId, toUpdateRequest(form))
      setProfile(saved)
      setForm(toFormState(saved))
      showSuccessToast('Profil enregistré. Vos prochains documents le porteront.')
    } catch (err) {
      // A 400 here is a field the endpoint refuses, and the user cannot guess which.
      // apiClient cannot name it -- ASP.NET answers with ValidationProblemDetails,
      // whose keys it does not read -- so the field list is parsed from the payload.
      if (err instanceof ApiError && err.status === 400) {
        showErrorToast(
          describeValidationError(err.response)
            ?? 'Un champ est refusé par le serveur.'
        )
      } else {
        showErrorToast("Erreur lors de l'enregistrement du profil.")
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-sm text-muted-foreground">Chargement…</div>

  if (loadFailed) {
    return (
      <Alert variant="destructive" data-testid="profile-load-error">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Profil illisible</AlertTitle>
        <AlertDescription>
          Le profil de votre société n’a pas pu être chargé. Rien n’a été modifié.{' '}
          <button type="button" className="underline underline-offset-4" onClick={load} data-testid="profile-retry">
            Réessayer
          </button>
        </AlertDescription>
      </Alert>
    )
  }

  const gaps = localGaps(form, profile?.name ?? '')
  const logoProblem = urlProblem(form.logoUrl)
  const websiteProblem = urlProblem(form.website)

  return (
    <div className="space-y-5" data-testid="settings-organization-profile">
      {gaps.length > 0 ? (
        <Alert variant="warning" data-testid="profile-gaps-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Vos documents sortent sans {describeGaps(gaps)}</AlertTitle>
          <AlertDescription>
            Vos devis et vos factures portent l’identité de votre société. Tant qu’il manque{' '}
            {describeGaps(gaps)}, ils sortiront sans.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert data-testid="profile-complete-alert">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Votre profil est complet</AlertTitle>
          <AlertDescription>
            Vos devis et vos factures portent votre raison sociale, votre adresse, votre numéro de
            TVA et votre compte bancaire.
          </AlertDescription>
        </Alert>
      )}

      {/* Raison sociale -- read-only on purpose: there is no rename endpoint, and the
          name is printed on every document. Saying so is more useful than hiding it. */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Raison sociale</h3>
        <Input value={profile?.name ?? ''} readOnly className="h-9 max-w-md text-sm bg-muted" data-testid="profile-name" />
        <p className="text-xs text-muted-foreground">
          Fixée à la création du compte et imprimée sur chaque devis et chaque facture. Pour la
          corriger, contactez-nous : elle ne se modifie pas depuis cet écran.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Siège social</h3>
        <p className="text-xs text-muted-foreground">En-tête de vos devis et de vos factures.</p>
        <div className="grid gap-3 sm:grid-cols-[1fr_110px_110px]">
          <div className="space-y-1">
            <Label htmlFor="profile-street" className="text-xs">Rue <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-street" value={form.street} onChange={e => set('street', e.target.value)} className="h-9 text-sm" data-testid="profile-street" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-number" className="text-xs">N° <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-number" value={form.number} onChange={e => set('number', e.target.value)} className="h-9 text-sm" data-testid="profile-number" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-box" className="text-xs">Boîte</Label>
            <Input id="profile-box" value={form.box} onChange={e => set('box', e.target.value)} className="h-9 text-sm" data-testid="profile-box" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-[110px_1fr_110px]">
          <div className="space-y-1">
            <Label htmlFor="profile-postal" className="text-xs">Code postal <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-postal" value={form.postalCode} onChange={e => set('postalCode', e.target.value)} className="h-9 text-sm" data-testid="profile-postal-code" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-city" className="text-xs">Commune <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-city" value={form.city} onChange={e => set('city', e.target.value)} className="h-9 text-sm" data-testid="profile-city" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-country" className="text-xs">Pays <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-country" value={form.country} onChange={e => set('country', e.target.value)} maxLength={2} className="h-9 text-sm" data-testid="profile-country" />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Identification</h3>
        <div className="grid gap-3 sm:grid-cols-2 max-w-2xl">
          <div className="space-y-1">
            <Label htmlFor="profile-vat" className="text-xs">Numéro de TVA <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-vat" value={form.vatNumber} onChange={e => set('vatNumber', e.target.value)} placeholder="BE 0123.456.789" className="h-9 text-sm" data-testid="profile-vat-number" />
            <p className="text-xs text-muted-foreground">Mention obligatoire sur une facture belge.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-phone" className="text-xs">Téléphone <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-phone" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+32 10 12 34 56" className="h-9 text-sm" data-testid="profile-phone" />
            <p className="text-xs text-muted-foreground">Format international, comme dans l’exemple.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-email" className="text-xs">E-mail de la société <span aria-hidden className="text-muted-foreground">*</span></Label>
            <Input id="profile-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} className="h-9 text-sm" data-testid="profile-email" />
            <p className="text-xs text-muted-foreground">
              Sur un devis, l’adresse du vendeur qui l’émet passe avant celle-ci.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile-website" className="text-xs">Site web</Label>
            <Input id="profile-website" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://…" className="h-9 text-sm" data-testid="profile-website" />
            {websiteProblem ? (
              <p className="text-xs text-destructive" data-testid="profile-website-error">{websiteProblem}</p>
            ) : null}
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Compte bancaire</h3>
        <p className="text-xs text-muted-foreground">
          Imprimé sur la ligne « montant à payer » de vos factures. Sans lui, la facture réclame un
          paiement sans dire où le verser.
        </p>
        <div className="space-y-2 max-w-md">
          {form.bankAccounts.map((account, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={account}
                onChange={e => setAccount(index, e.target.value)}
                placeholder="BE68 5390 0754 7034"
                className="h-9 text-sm"
                data-testid={`profile-bank-account-${index}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeAccount(index)}
                aria-label="Supprimer ce compte"
                data-testid={`profile-bank-account-remove-${index}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addAccount} data-testid="profile-bank-account-add">
            <Plus className="size-4" /> Ajouter un compte
          </Button>
          <p className="text-xs text-muted-foreground">
            Le premier compte est celui imprimé sur vos factures.
          </p>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Logo</h3>
        <Input
          value={form.logoUrl}
          onChange={e => set('logoUrl', e.target.value)}
          placeholder="https://…/logo.png"
          className="h-9 max-w-md text-sm"
          data-testid="profile-logo-url"
        />
        <p className="text-xs text-muted-foreground">
          L’adresse d’une image en ligne. Sans logo, vos documents impriment votre raison sociale en
          toutes lettres — c’est prévu, ce n’est pas un défaut.
        </p>
        {logoProblem ? (
          <p className="text-xs text-destructive" data-testid="profile-logo-error">{logoProblem}</p>
        ) : null}
        {form.logoUrl.trim() && !logoProblem && !logoBroken ? (
          <img
            src={form.logoUrl}
            alt="Logo de la société"
            className="mt-2 max-h-16 w-auto"
            data-testid="profile-logo-preview"
            onError={() => setLogoBroken(true)}
          />
        ) : null}
      </section>

      <p className="text-xs text-muted-foreground">
        Les champs marqués <span aria-hidden>*</span> sont obligatoires pour enregistrer.
      </p>

      <div className="pt-1">
        <Button onClick={handleSave} disabled={saving} size="sm" data-testid="profile-save">
          {saving ? 'Enregistrement…' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  )
}
